import { gql, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import {
  converEditorContentToTranscriptNSA,
  convertEditorToDownloadableFormat,
  TifmEditor,
} from '@northshoreautomation/tifm-editor';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { omit } from 'lodash';
import moment from 'moment';
import { type ChangeEvent, type FocusEvent, useEffect, useState } from 'react';
import updateTranscript from 'src/api/transcript/mutations';
import { INSERT_MODE, VISUAL_MODE } from 'src/constants';
import { AssetNodeTypeNoPerms, assetQuery } from 'src/dynamoQueries/assets';
import { buildAssetTimecodeMetaQuery } from 'src/dynamoQueries/timecodes';
import { transcriptQuery } from 'src/dynamoQueries/transcript';
import { isVisualMode, setGlobalStateMode } from 'src/helpers/mode';
import viewStateVar from 'src/store/viewState';
import theme from 'src/theme';

const JSON_FORMAT = 'JSON';

interface Linguistic {
  __typename?: string;
  [key: string]: unknown;
}

interface Expression {
  __typename?: string;
  orderNumber?: string;
  ordinal?: string;
  linguistics: Linguistic[];
  [key: string]: unknown;
}

interface TranscriptMeta {
  [key: string]: unknown;
}

interface TranscriptData {
  expressions: Expression[];
  meta: TranscriptMeta;
}

interface AssetData {
  asset: {
    id: string;
    name: string;
    timecode: string;
    inpoint: string;
    [key: string]: unknown;
  };
}

interface AssetTimecodeMetadataData {
  assetTimecodeMetadata: {
    framerate: string | null;
    dropframe: boolean | null;
    inTimecode: string | null;
  };
}

interface TranscriptQueryData {
  transcript: string;
}

interface VideoJsPlayer {
  currentTime?: number | (() => number);
}

interface TranscriptProps {
  player: VideoJsPlayer;
  assetId: string;
}

interface EditorValue {
  [key: string]: unknown;
}

interface SaveParams {
  nsaTranscriptJSON?: string;
}

const Transcript: React.FC<TranscriptProps> = ({ player, assetId }) => {
  const viewState = useReactiveVar(viewStateVar);
  const [editView, setEditView] = useState(false);
  const [_lastTimeChanged, setLastTimeChanged] = useState<string | null>(null);
  const [_lastTimeSaved, setLastTimeSaved] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptData | null>(null);

  const buildQuery = () => {
    const q = transcriptQuery();
    q.query.transcript.__args.assetId = assetId;
    const gQuery = jsonToGraphQLQuery(q, { pretty: true });
    return gql(gQuery);
  };

  const [saveTranscript, { loading: savingTranscr, error: savingError }] = useMutation(updateTranscript, {
    refetchQueries: [buildQuery()],
  });

  const { loading, error, data } = useQuery<TranscriptQueryData>(buildQuery());

  const buildAssetQuery = () => {
    const asn = AssetNodeTypeNoPerms(false);
    asn.timecode = true;
    asn.inpoint = true;
    asn.id = true;
    const q = assetQuery();
    q.query.asset = { ...q.query.asset, ...asn };
    q.query.asset.__args.id = assetId;
    const gQuery = jsonToGraphQLQuery(q, { pretty: true });
    return gql(gQuery);
  };

  useEffect(() => {
    if (data?.transcript) {
      const parseddata = JSON.parse(data.transcript) as TranscriptData;
      setTranscript(parseddata);
    }
  }, [data]);

  const { data: asset, loading: assetLoading, error: assetError } = useQuery<AssetData>(buildAssetQuery());

  const assetTcMetaQuery = buildAssetTimecodeMetaQuery(false, (m) => {
    m.framerate = true;
    m.dropframe = true;
    m.inTimecode = true;
  });

  const { data: assetTcMeta } = useQuery<AssetTimecodeMetadataData>(assetTcMetaQuery, {
    variables: { assetId },
  });

  const onViewChange = (isEditView: boolean): void => {
    setEditView(isEditView);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: viewState.mode is intentionally excluded to prevent unnecessary mode toggling
  useEffect(() => {
    if (editView && isVisualMode(viewState.mode)) {
      return setGlobalStateMode(INSERT_MODE)();
    }

    if (!editView && !isVisualMode(viewState.mode)) {
      return setGlobalStateMode(VISUAL_MODE)();
    }

    return () => {
      setGlobalStateMode(VISUAL_MODE)();
    };
  }, [editView]);

  const sanitizeExpressionsPayload = ({ expressions }: { expressions: Expression[] }): Expression[] => {
    return expressions.map((exp, idx) => {
      const cleanedExp = omit(exp, ['__typename']) as Expression;
      cleanedExp.orderNumber = String(idx);
      cleanedExp.ordinal = String(idx);

      const cleanedLings = cleanedExp.linguistics.map((ling) => omit(ling, ['__typename']) as Linguistic);
      cleanedExp.linguistics = cleanedLings;
      return cleanedExp;
    });
  };

  const onSave = ({ nsaTranscriptJSON }: SaveParams): boolean => {
    setLastTimeSaved(new Date().toISOString());
    const parsedTranscript = nsaTranscriptJSON ? (JSON.parse(nsaTranscriptJSON) as TranscriptData) : null;

    if (parsedTranscript) {
      const { expressions, meta } = parsedTranscript;
      if (expressions) {
        const payload = {
          expressions: sanitizeExpressionsPayload({ expressions }),
          meta,
        };

        saveTranscript({
          variables: {
            transcript: payload,
            assetId: assetId,
          },
        });
      }
    }
    return true;
  };

  const onChange = (): void => {
    setLastTimeChanged(new Date().toISOString());
  };

  const onDownload = (e: ChangeEvent<HTMLSelectElement>, editorValue: EditorValue): void => {
    const formatValue = e.target.value;
    if (!asset?.asset) return;

    if (assetError) {
      alert('asset fetching errored, refresh the browser and try again');
      return;
    }

    // Framerate, dropframe flag, and normalized start timecode are resolved
    // server-side by gather_required_timecode_meta_from_asset, which handles
    // the legacy `timecode`-field-stores-fps convention and falls back to
    // 29.97 dropframe when metadata is missing or malformed.
    const tcMeta = assetTcMeta?.assetTimecodeMetadata;
    const fps = Number(tcMeta?.framerate);
    const timecodeIn = tcMeta?.inTimecode;
    const dropframe = tcMeta?.dropframe ?? false;

    if (!timecodeIn || !Number.isFinite(fps) || fps <= 0) {
      alert(
        'Could not determine framerate or start timecode for this asset. ' +
          'Please check the asset metadata (timecode / inpoint) and try again.',
      );
      return;
    }

    const downloadableFormat =
      formatValue === JSON_FORMAT
        ? JSON.stringify(converEditorContentToTranscriptNSA(editorValue))
        : convertEditorToDownloadableFormat({
            editorValue,
            formatValue,
            timecodeIn,
            fps,
            dropframe,
          }).join('\n');

    downloadTxtFile(downloadableFormat, formatValue);
  };

  const downloadTxtFile = (downloadableFormat: string, formatValue: string): void => {
    if (!asset?.asset) return;

    const { id, name } = asset.asset;
    const element = document.createElement('a');
    const file = new Blob([downloadableFormat], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${id}_${name}_${formatValue === 'AVID' ? 'nle_transcript' : 'transcript'}.txt`;
    document.body.appendChild(element);
    element.click();
  };

  const onBlurHandler = (_e: FocusEvent<HTMLInputElement>): void => {
    if (!editView && viewStateVar().mode !== VISUAL_MODE) {
      setGlobalStateMode(VISUAL_MODE);
    }
  };

  const onFocusHandler = (_e: FocusEvent<HTMLInputElement>): void => {
    if (viewStateVar().mode !== INSERT_MODE) {
      setGlobalStateMode(INSERT_MODE);
    }
  };

  if (assetLoading) return <div>fetching asset meta....</div>;
  if (loading || !player || !transcript) return <CircularProgress color="secondary" />;
  if (error || savingError) return <div>`Error! ${JSON.stringify(error)}`</div>;

  return (
    <>
      {/* Note: Navigation blocking handled by window.onbeforeunload in parent components */}
      <TifmEditor
        autoScroll={true}
        onViewChange={onViewChange}
        onSave={onSave}
        onDownload={onDownload}
        onChange={onChange}
        videoPlayer={player}
        transcript={transcript}
        isSaving={savingTranscr ? 'Saving...' : ''}
        button={<Button variant="outlined" size="small" style={{ marginRight: '8px', marginTop: '4px' }}></Button>}
        checkbox={<Checkbox size="small" />}
        select={
          <select
            style={{
              marginTop: '4px',
              marginLeft: '-8px',
              marginRight: '8px',
              padding: '5px 10px',
              fontFamily: 'avenir, sans-serif',
              fontSize: '0.8125rem',
              color: theme.palette?.text?.primary ?? '#fff',
              backgroundColor: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.23)',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          />
        }
        textField={
          <TextField
            onFocus={onFocusHandler}
            onBlur={onBlurHandler}
            size="small"
            variant="outlined"
            style={{ marginRight: '8px', width: '260px' }}
          />
        }
        currentTime={player?.currentTime}
        theme={{
          highlight: (theme.palette?.primary as { light?: string })?.light ?? '#3698CF',
          searchHighlight: (theme.palette?.error as { main?: string })?.main ?? '#F04354',
          fontColor: theme.palette?.text?.primary ?? '#ececec',
          bg: theme.palette?.background?.paper ?? '#232323',
          fill: theme.palette?.text?.primary ?? '#fff',
          speaker: (theme.palette?.primary as { main?: string })?.main ?? '#BB86FC',
          speakerActive: (theme.palette?.secondary as { main?: string })?.main ?? '#57B5AF',
          font: 'avenir',
          saveBtn: {
            color: (theme.palette?.primary as { contrastText?: string })?.contrastText ?? '#ffffff',
            background: (theme.palette?.primary as { main?: string })?.main ?? '#BB86FC',
            margin: '4px 8px 0 0',
          },
        }}
      />
      <span>version: {TifmEditor.version}</span>
    </>
  );
};

export default Transcript;

const _promptWhen = (lastTimeChanged: string | null, lastTimeSaved: string | null): boolean => {
  if (lastTimeChanged !== null && lastTimeSaved === null) {
    return true;
  }

  if (lastTimeChanged === null) {
    return false;
  }

  if (moment(lastTimeChanged).isAfter(moment(lastTimeSaved))) {
    return true;
  }

  return false;
};
